import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Button,
  Space,
  Input,
  Divider,
  Typography,
  theme,
  Select,
  InputNumber,
  Form,
  Collapse,
  Switch,
  message,
  Modal,
  Row,
  Col,
  Tooltip,
  Steps,
} from 'antd';
import type { WorkflowFormData, WorkflowStep } from '../types/workflow.types';
import { StepTypeEnum, FindByEnum, DataSourceEnum } from '../types/workflow.types';
import { useWorkflowForm } from '../hooks/use-workflow-form.hook';
import { requiresSelector, requiresValue } from '@const/workflow.constants';
import { ApplicationKeys, acronyms } from '@const/get-allowed-application-keys.const';
import {
  WorkflowConditionKeys,
  WORKFLOW_CONDITION_SKIP_RE_LOGIN,
} from '@const/workflow-condition-keys.const';
import { Controller } from 'react-hook-form';
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  EyeOutlined,
  CopyOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { TextArea } = Input;
const { Panel } = Collapse;

// Format camelCase to Title Case (e.g., "passportNumber" -> "Passport Number")
const formatLabel = (key: string): string => {
  // Convert camelCase to Title Case with proper spacing
  const formatted = key
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .split(' ')
    .map((word) => {
      const lowerWord = word.toLowerCase();
      // If word is an acronym, return uppercase
      if (acronyms.includes(lowerWord)) {
        return lowerWord.toUpperCase();
      }
      // Otherwise capitalize first letter
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');

  return formatted.trim();
};

// Format snake_case workflow condition keys to a readable label (e.g., "skip_re_login" -> "Skip Re Login")
const formatConditionLabel = (key: string): string => {
  return key
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

interface WorkflowFormProps {
  defaultValues?: WorkflowFormData;
  onSubmit: (data: WorkflowFormData) => void;
  isLoading?: boolean;
}

const WorkflowForm: React.FC<WorkflowFormProps> = ({ defaultValues, onSubmit, isLoading }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [jsonModalVisible, setJsonModalVisible] = useState(false);
  const [generatedJson, setGeneratedJson] = useState<string>('');
  const [activeCollapseKey, setActiveCollapseKey] = useState<string | string[]>('0');
  const [currentStep, setCurrentStep] = useState(0);

  const { control, handleSubmit, errors, watch, setValue, trigger } = useWorkflowForm({
    defaultValues,
    onSubmit: (data) => {
      onSubmit(data);
    },
  });

  // Watch the entire form to detect all changes, including nested ones
  const formValues = watch();
  const watchedSteps = formValues?.steps || [];
  const watchedName = formValues?.name;
  const conditionKeys = useMemo(() => {
    if (!formValues?.condition || typeof formValues.condition !== 'object') {
      return [];
    }
    return Object.keys(formValues.condition);
  }, [formValues?.condition]);

  // Check if step 1 is valid (name and all steps have required fields)
  // Using formValues in dependency to ensure reactivity to all nested changes
  const isStep1Valid = useMemo(() => {
    const steps = formValues?.steps || [];
    const name = formValues?.name;

    if (!name || name.trim() === '') {
      return false;
    }
    if (!steps || steps.length === 0) {
      return false;
    }
    // Check if all steps have required fields: stepName, type, and if required: find.by, find.value
    return steps.every((step) => {
      if (!step.stepName || step.stepName.trim() === '') {
        return false;
      }
      if (!step.step.type) {
        return false;
      }
      // Only validate find.by and find.value for step types that require a selector
      // For step types without selector requirement (NAVIGATE, WAIT, CLOSE, CAPTCHA, PAUSE),
      // empty strings for find.by and find.value are valid
      if (requiresSelector(step.step.type)) {
        const findBy = step.step.find?.by;
        const findValue = step.step.find?.value;
        // Check if findBy is empty string or falsy, and findValue is empty string or falsy
        if (!findBy || String(findBy) === '' || !findValue || String(findValue) === '') {
          return false;
        }
      }
      // For step types that don't require selector, find.by and find.value can be empty strings
      // No validation needed - they are always valid
      if (requiresValue(step.step.type)) {
        // Value field validation:
        // - undefined: valid (treated as dynamic/optional)
        // - function: valid (dynamic)
        // - empty string '': valid (treated as dynamic/optional)
        // - non-empty string: valid (static value)
        // - Only invalid: non-empty string that is only whitespace (has length > 0 but trim is empty)
        const value = step.step.value;
        // If value is a string and has length > 0 but becomes empty after trim, it's invalid
        if (typeof value === 'string' && value.length > 0 && value.trim() === '') {
          // Non-empty string that is only whitespace - invalid
          return false;
        }
        // All other cases are valid: undefined, function, empty string '', or valid non-empty string
      }
      return true;
    });
  }, [formValues]);

  // Detect dynamic fields: steps with stepName and dynamic value
  const dynamicFields = useMemo(() => {
    const steps = formValues?.steps || [];
    return steps
      .map((step, index) => ({ step, index }))
      .filter(({ step }) => {
        const hasStepName = step.stepName && step.stepName.trim() !== '';
        const hasDynamicValue =
          step.step.value === undefined || typeof step.step.value === 'function';
        const requiresValueField = requiresValue(step.step.type);
        return hasStepName && hasDynamicValue && requiresValueField;
      })
      .map(({ step, index }) => ({
        stepName: step.stepName!,
        stepOrder: step.order,
        index,
        key: `step-${step.order}-${index}`, // Unique key using step order
      }))
      .filter(
        (item): item is { stepName: string; stepOrder: number; index: number; key: string } =>
          item.stepName !== undefined && item.stepName.trim() !== ''
      );
  }, [formValues]);

  // Clean up mappings when dynamic fields change - remove entries for steps that are no longer dynamic
  // Only run cleanup when on step 2 to avoid clearing mappings when editing in step 1
  useEffect(() => {
    // Don't run cleanup if we're on step 1 - preserve mappings during editing
    if (currentStep !== 1) {
      return;
    }

    const currentMappings = watch('mappings');
    // Don't run cleanup if mappings don't exist or fieldsMap is missing/empty
    // This prevents clearing valid mappings during initial load
    if (!currentMappings?.fieldsMap || Object.keys(currentMappings.fieldsMap).length === 0) {
      return;
    }

    const dynamicStepOrders = new Set(dynamicFields.map((field) => String(field.stepOrder)));
    const fieldsMap = currentMappings.fieldsMap;
    const fieldsMapKeys = Object.keys(fieldsMap);

    // Check if there are any mappings for steps that are no longer dynamic
    const hasStaleMappings = fieldsMapKeys.some((key) => !dynamicStepOrders.has(key));

    if (hasStaleMappings) {
      // Create a new fieldsMap with only entries for currently dynamic steps
      // Preserve existing mappings for steps that are still dynamic
      const cleanedFieldsMap: Record<string, string> = {};
      dynamicStepOrders.forEach((stepOrder) => {
        if (fieldsMap[stepOrder]) {
          cleanedFieldsMap[stepOrder] = fieldsMap[stepOrder];
        }
      });

      setValue('mappings', {
        ...currentMappings,
        fieldsMap: cleanedFieldsMap,
      });
    }
  }, [dynamicFields, watch, setValue, currentStep]);

  // Auto-update JSON preview whenever form data changes
  useEffect(() => {
    // Clean steps for JSON preview
    const cleanStepsForPreview = (steps: any[]) => {
      return steps.map((step) => {
        const cleanedStep = JSON.parse(JSON.stringify(step)); // Deep copy
        // Remove iframeContext if not enabled
        if (!cleanedStep.step.iframeContext?.autoDetect) {
          delete cleanedStep.step.iframeContext;
        }
        // Ensure find always exists with empty strings if step type doesn't require selector
        if (cleanedStep.step.type && !requiresSelector(cleanedStep.step.type)) {
          if (!cleanedStep.step.find) {
            cleanedStep.step.find = { by: '', value: '' };
          } else {
            cleanedStep.step.find.by = cleanedStep.step.find.by || '';
            cleanedStep.step.find.value = cleanedStep.step.find.value || '';
          }
        }
        return cleanedStep;
      });
    };

    // Initial JSON generation
    const formData = watch();
    const cleanedSteps = cleanStepsForPreview(formData.steps || []);
    const initialJson = JSON.stringify(cleanedSteps, null, 2);
    setGeneratedJson(initialJson);

    // Subscribe to changes
    const subscription = watch((formData) => {
      const cleanedSteps = cleanStepsForPreview(formData.steps || []);
      const jsonOutput = JSON.stringify(cleanedSteps, null, 2);
      setGeneratedJson(jsonOutput);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  // Add a new step
  const addStep = () => {
    const currentSteps = watchedSteps;
    const stepType = StepTypeEnum.NAVIGATE;
    const newStep: WorkflowStep = {
      order: currentSteps.length + 1,
      step: {
        type: stepType,
        // Always include find, with empty strings if step type doesn't require selector
        find: requiresSelector(stepType)
          ? {
              by: FindByEnum.CSS,
              value: '',
            }
          : {
              by: '',
              value: '',
            },
      },
      delayMs: 1000,
    };

    setValue('steps', [...currentSteps, newStep]);
    // Open the newly added step
    setActiveCollapseKey(String(currentSteps.length));
  };

  // Remove a step
  const removeStep = (index: number) => {
    const currentSteps = watchedSteps;
    const newSteps = currentSteps.filter((_, i) => i !== index);

    // Reorder the remaining steps
    const reorderedSteps = newSteps.map((step, i) => ({
      ...step,
      order: i + 1,
    }));

    setValue('steps', reorderedSteps);

    // Update active key after deletion
    if (reorderedSteps.length === 0) {
      setActiveCollapseKey('');
    } else if (index > 0) {
      setActiveCollapseKey(String(index - 1));
    } else {
      setActiveCollapseKey('0');
    }
  };

  // Move step up
  const moveStepUp = (index: number) => {
    if (index === 0) return;
    const currentSteps = [...watchedSteps];
    [currentSteps[index - 1], currentSteps[index]] = [currentSteps[index], currentSteps[index - 1]];

    // Reorder
    const reorderedSteps = currentSteps.map((step, i) => ({
      ...step,
      order: i + 1,
    }));

    setValue('steps', reorderedSteps);
  };

  // Move step down
  const moveStepDown = (index: number) => {
    if (index === watchedSteps.length - 1) return;
    const currentSteps = [...watchedSteps];
    [currentSteps[index], currentSteps[index + 1]] = [currentSteps[index + 1], currentSteps[index]];

    // Reorder
    const reorderedSteps = currentSteps.map((step, i) => ({
      ...step,
      order: i + 1,
    }));

    setValue('steps', reorderedSteps);
  };

  // Generate and show JSON
  const showJsonOutput = () => {
    const formData = watch();
    const jsonOutput = JSON.stringify(formData.steps, null, 2);
    setGeneratedJson(jsonOutput);
    setJsonModalVisible(true);
  };

  // Copy JSON to clipboard
  const copyJsonToClipboard = () => {
    navigator.clipboard.writeText(generatedJson);
    message.success('JSON copied to clipboard!');
  };

  // Handle step navigation
  const handleNext = async () => {
    if (currentStep === 0) {
      // Validate step 1 before proceeding
      const isValid = await trigger(['name', 'steps']);
      if (isValid) {
        // Initialize mappings structure if it doesn't exist and there are dynamic fields
        const currentMappings = watch('mappings');
        if (dynamicFields.length > 0) {
          if (!currentMappings) {
            // Create new mappings structure
            setValue('mappings', {
              dataSource: DataSourceEnum.APPLICATION,
              fieldsMap: {},
            });
          } else {
            // Preserve existing fieldsMap values - only update if fieldsMap is missing or invalid
            const existingFieldsMap = currentMappings.fieldsMap || {};
            const needsFieldsMapUpdate =
              !currentMappings.fieldsMap || typeof currentMappings.fieldsMap !== 'object';
            const needsDataSourceUpdate = !currentMappings.dataSource;

            // Only update if something needs to be fixed, preserving all existing values including _id
            if (needsFieldsMapUpdate || needsDataSourceUpdate) {
              setValue('mappings', {
                ...currentMappings,
                fieldsMap: needsFieldsMapUpdate ? existingFieldsMap : currentMappings.fieldsMap,
                dataSource: needsDataSourceUpdate
                  ? DataSourceEnum.APPLICATION
                  : currentMappings.dataSource,
              });
            }
            // If mappings exist and are valid, don't touch them - they should already be preserved
          }
        }
        setCurrentStep(1);
      }
    } else if (currentStep === 1) {
      // Move from Step 2 (Link Dynamic Fields) to Step 3 (Apply Condition)
      setCurrentStep(2);
    }
  };

  const handlePrevious = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    } else {
      setCurrentStep(0);
    }
  };

  const steps = [
    {
      title: t('workflow.createStep.title', { defaultValue: 'Create Workflow' }),
    },
    {
      title: t('workflow.linkDynamicFields'),
    },
    {
      title: t('workflow.applyCondition.title', { defaultValue: 'Apply Condition' }),
    },
  ];

  return (
    <>
      <Row gutter={16}>
        <Col xs={24} lg={14} xl={16}>
          <Card className="shadow-lg" style={{ marginBottom: 24 }}>
            <Steps current={currentStep} items={steps} />
          </Card>
          <Card className="shadow-lg">
            <Form layout="vertical">
              {currentStep === 0 ? (
                <>
                  {/* Step 1: Create Workflow */}
                  {/* Basic Info Section */}
                  <Typography.Title level={4}>
                    {t('workflow.configuration.title', { defaultValue: 'Workflow Configuration' })}
                  </Typography.Title>
                  <Divider />

                  <Form.Item
                    label={t('workflow.name.label', { defaultValue: 'Workflow Name' })}
                    required
                  >
                    <Controller
                      name="name"
                      control={control}
                      rules={{
                        required: t('workflow.name.required', { defaultValue: 'Name is required' }),
                      }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          placeholder={t('workflow.name.placeholder', {
                            defaultValue: 'Enter workflow name',
                          })}
                          status={errors.name ? 'error' : ''}
                        />
                      )}
                    />
                    {errors.name && (
                      <div style={{ color: token.colorError, marginTop: 4 }}>
                        {errors.name.message}
                      </div>
                    )}
                  </Form.Item>

                  {/* Steps Section */}
                  <Divider />
                  <Typography.Title level={4}>{t('workflow.steps.label')}</Typography.Title>

                  {watchedSteps.length === 0 && (
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '40px',
                        background: token.colorBgContainer,
                        border: `1px dashed ${token.colorBorder}`,
                        borderRadius: token.borderRadius,
                      }}
                    >
                      <Typography.Text type="secondary">
                        {t('workflow.noSteps.message')}
                      </Typography.Text>
                    </div>
                  )}

                  <Collapse accordion activeKey={activeCollapseKey} onChange={setActiveCollapseKey}>
                    {watchedSteps.map((step, index) => {
                      const stepName = watch(`steps.${index}.stepName` as any) as
                        | string
                        | undefined;
                      const displayName = stepName && stepName.trim() ? stepName : '';
                      const headerText = displayName
                        ? `Step ${step.order}: ${displayName}`
                        : `Step ${step.order}`;
                      return (
                        <Panel
                          header={headerText}
                          key={index}
                          extra={
                            <Space onClick={(e) => e.stopPropagation()}>
                              <Tooltip title="Move step up">
                                <Button
                                  size="small"
                                  icon={<ArrowUpOutlined />}
                                  disabled={index === 0}
                                  onClick={() => moveStepUp(index)}
                                />
                              </Tooltip>
                              <Tooltip title="Move step down">
                                <Button
                                  size="small"
                                  icon={<ArrowDownOutlined />}
                                  disabled={index === watchedSteps.length - 1}
                                  onClick={() => moveStepDown(index)}
                                />
                              </Tooltip>
                              <Tooltip title="Delete step">
                                <Button
                                  size="small"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => removeStep(index)}
                                />
                              </Tooltip>
                            </Space>
                          }
                        >
                          <Space direction="vertical" style={{ width: '100%' }} size="middle">
                            {/* Step Name Field */}
                            <Form.Item label={t('workflow.stepName.label')} required>
                              <Controller
                                name={`steps.${index}.stepName` as any}
                                control={control}
                                rules={{ required: 'Step name is required' }}
                                render={({ field }) => (
                                  <Input
                                    {...field}
                                    placeholder={t('workflow.stepName.placeholder')}
                                    status={errors.steps?.[index]?.stepName ? 'error' : ''}
                                  />
                                )}
                              />
                              {errors.steps?.[index]?.stepName && (
                                <div style={{ color: token.colorError, marginTop: 4 }}>
                                  {errors.steps[index]?.stepName?.message as string}
                                </div>
                              )}
                            </Form.Item>

                            {(() => {
                              const stepType = watch(
                                `steps.${index}.step.type` as any
                              ) as StepTypeEnum;
                              const showSelectorFields = stepType && requiresSelector(stepType);
                              const showValueField = stepType && requiresValue(stepType);

                              return (
                                <>
                                  {/* Step Type and Find By in one row */}
                                  <Row gutter={16}>
                                    <Col xs={24} sm={showSelectorFields ? 12 : 24}>
                                      <Form.Item
                                        label={t('workflow.stepType.label', {
                                          defaultValue: 'Step Type',
                                        })}
                                        required
                                      >
                                        <Controller
                                          name={`steps.${index}.step.type` as any}
                                          control={control}
                                          rules={{
                                            required: t('workflow.stepType.required', {
                                              defaultValue: 'Type is required',
                                            }),
                                          }}
                                          render={({ field }) => (
                                            <Select
                                              {...field}
                                              placeholder={t('workflow.stepType.placeholder', {
                                                defaultValue: 'Select step type',
                                              })}
                                              onChange={(value) => {
                                                field.onChange(value);
                                                const newStepType = value as StepTypeEnum;
                                                // Clear find by, selector value, and field value when step type changes
                                                if (requiresSelector(newStepType)) {
                                                  // If new type requires selector, set default find
                                                  setValue(`steps.${index}.step.find` as any, {
                                                    by: FindByEnum.CSS,
                                                    value: '',
                                                  });
                                                } else {
                                                  // If new type doesn't require selector, set empty strings
                                                  setValue(`steps.${index}.step.find` as any, {
                                                    by: '',
                                                    value: '',
                                                  });
                                                }
                                                setValue(
                                                  `steps.${index}.step.value` as any,
                                                  undefined
                                                );
                                              }}
                                            >
                                              {Object.values(StepTypeEnum).map((type) => (
                                                <Select.Option key={type} value={type}>
                                                  {type}
                                                </Select.Option>
                                              ))}
                                            </Select>
                                          )}
                                        />
                                      </Form.Item>
                                    </Col>
                                    {showSelectorFields && (
                                      <Col xs={24} sm={12}>
                                        <Form.Item
                                          label={t('workflow.findBy.label', {
                                            defaultValue: 'Find By',
                                          })}
                                          required
                                        >
                                          <Controller
                                            name={`steps.${index}.step.find.by` as any}
                                            control={control}
                                            rules={{
                                              required: showSelectorFields
                                                ? t('workflow.findBy.required', {
                                                    defaultValue: 'Find by is required',
                                                  })
                                                : false,
                                            }}
                                            render={({ field }) => (
                                              <Select
                                                {...field}
                                                placeholder={t('workflow.findBy.placeholder', {
                                                  defaultValue: 'Select find method',
                                                })}
                                              >
                                                {Object.values(FindByEnum).map((by) => (
                                                  <Select.Option key={by} value={by}>
                                                    {by}
                                                  </Select.Option>
                                                ))}
                                              </Select>
                                            )}
                                          />
                                        </Form.Item>
                                      </Col>
                                    )}
                                  </Row>

                                  {/* Selector Value and Delay in one row */}
                                  <Row gutter={16}>
                                    {showSelectorFields && (
                                      <Col xs={24} sm={12}>
                                        <Form.Item
                                          label={t('workflow.selectorValue.label', {
                                            defaultValue: 'Selector Value',
                                          })}
                                          required
                                        >
                                          <Controller
                                            name={`steps.${index}.step.find.value` as any}
                                            control={control}
                                            rules={{
                                              required: showSelectorFields
                                                ? t('workflow.selectorValue.required', {
                                                    defaultValue: 'Selector value is required',
                                                  })
                                                : false,
                                            }}
                                            render={({ field }) => (
                                              <Input
                                                {...field}
                                                placeholder={t(
                                                  'workflow.selectorValue.placeholder',
                                                  {
                                                    defaultValue:
                                                      'Enter selector value (e.g., #username, .btn-submit)',
                                                  }
                                                )}
                                              />
                                            )}
                                          />
                                        </Form.Item>
                                      </Col>
                                    )}
                                    <Col xs={24} sm={showSelectorFields ? 12 : 24}>
                                      <Form.Item
                                        label={t('workflow.delayMs.label', {
                                          defaultValue: 'Delay (milliseconds)',
                                        })}
                                      >
                                        <Controller
                                          name={`steps.${index}.delayMs` as any}
                                          control={control}
                                          render={({ field }) => (
                                            <InputNumber
                                              {...field}
                                              style={{ width: '100%' }}
                                              min={0}
                                              step={100}
                                              placeholder={t('workflow.delayMs.placeholder', {
                                                defaultValue: 'Enter delay in milliseconds',
                                              })}
                                            />
                                          )}
                                        />
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                </>
                              );
                            })()}

                            {/* Field Value with Dynamic Value Checkbox */}
                            {(() => {
                              const stepType = watch(
                                `steps.${index}.step.type` as any
                              ) as StepTypeEnum;
                              const showValueField = stepType && requiresValue(stepType);

                              if (!showValueField) {
                                return null;
                              }

                              const stepValue = watch(`steps.${index}.step.value` as any);
                              const isDynamic =
                                stepValue === undefined || typeof stepValue === 'function';
                              return (
                                <Row gutter={16} align="middle">
                                  <Col xs={24} sm={24}>
                                    <Form.Item
                                      label={t('workflow.fieldValue.label', {
                                        defaultValue: 'Field Value',
                                      })}
                                      required={!isDynamic}
                                      style={{ marginBottom: 0 }}
                                      validateStatus={
                                        errors.steps?.[index]?.step?.value ? 'error' : ''
                                      }
                                      help={
                                        errors.steps?.[index]?.step?.value?.message as
                                          | string
                                          | undefined
                                      }
                                    >
                                      <Row gutter={8} align="middle">
                                        <Col flex="auto">
                                          <Controller
                                            name={`steps.${index}.step.value` as any}
                                            control={control}
                                            rules={{
                                              validate: (value) => {
                                                // Check if value is dynamic (undefined or function)
                                                const isValueDynamic =
                                                  value === undefined ||
                                                  typeof value === 'function';

                                                // If not dynamic and value is empty, it's required
                                                if (!isValueDynamic) {
                                                  if (!value || value === '') {
                                                    return t('workflow.fieldValue.required', {
                                                      defaultValue: 'Field value is required',
                                                    });
                                                  }
                                                }
                                                return true;
                                              },
                                            }}
                                            render={({ field, fieldState }) => (
                                              <Input
                                                disabled={isDynamic}
                                                placeholder={t('workflow.fieldValue.placeholder', {
                                                  defaultValue: 'Enter field value (e.g., Fatima)',
                                                })}
                                                value={
                                                  isDynamic ? '' : (field.value as string) || ''
                                                }
                                                onChange={(e) => {
                                                  if (!isDynamic) {
                                                    field.onChange(e.target.value);
                                                  }
                                                }}
                                                status={fieldState.error ? 'error' : ''}
                                              />
                                            )}
                                          />
                                        </Col>
                                        <Col>
                                          <Switch
                                            checked={isDynamic}
                                            onChange={(checked) => {
                                              if (checked) {
                                                // Set to undefined for dynamic value and clear the input
                                                setValue(
                                                  `steps.${index}.step.value` as any,
                                                  undefined,
                                                  {
                                                    shouldValidate: false,
                                                  }
                                                );
                                              } else {
                                                // Clear and set to empty string for static value
                                                setValue(`steps.${index}.step.value` as any, '', {
                                                  shouldValidate: false,
                                                });
                                              }
                                            }}
                                          />
                                        </Col>
                                        <Col>
                                          <span style={{ marginLeft: 8 }}>
                                            {t('workflow.fieldValue.useDynamic', {
                                              defaultValue: 'Use dynamic value',
                                            })}
                                          </span>
                                        </Col>
                                      </Row>
                                    </Form.Item>
                                  </Col>
                                </Row>
                              );
                            })()}

                            {/* Extract Attribute Field (only for extract step type) */}
                            {(() => {
                              const stepType = watch(
                                `steps.${index}.step.type` as any
                              ) as StepTypeEnum;
                              const isExtractStep = stepType === StepTypeEnum.EXTRACT;

                              if (!isExtractStep) {
                                return null;
                              }

                              return (
                                <Row gutter={16}>
                                  <Col xs={24} sm={24}>
                                    <Form.Item
                                      label={t('workflow.extractAttribute.label', {
                                        defaultValue: 'Extract Attribute',
                                      })}
                                      help={t('workflow.extractAttribute.help', {
                                        defaultValue:
                                          'Specify what to extract: "text", "value", "innerText", "selectedValue", "selectedText", or any HTML attribute (e.g., "href", "src", "data-id"). Leave empty for auto-detection.',
                                      })}
                                    >
                                      <Controller
                                        name={`steps.${index}.step.extractAttribute` as any}
                                        control={control}
                                        render={({ field }) => (
                                          <Input
                                            {...field}
                                            placeholder={t(
                                              'workflow.extractAttribute.placeholder',
                                              {
                                                defaultValue:
                                                  'e.g., text, value, href, selectedValue',
                                              }
                                            )}
                                            allowClear
                                          />
                                        )}
                                      />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              );
                            })()}

                            {/* Extract Columns Field (only for extractTable step type) */}
                            {(() => {
                              const stepType = watch(
                                `steps.${index}.step.type` as any
                              ) as StepTypeEnum;
                              const isExtractTableStep = stepType === StepTypeEnum.EXTRACT_TABLE;

                              if (!isExtractTableStep) {
                                return null;
                              }

                              return (
                                <Row gutter={16}>
                                  <Col xs={24} sm={24}>
                                    <Form.Item
                                      label={t('workflow.extractColumns.label', {
                                        defaultValue: 'Extract Columns',
                                      })}
                                      help={t('workflow.extractColumns.help', {
                                        defaultValue:
                                          'Specify which column headers to extract (e.g., "Passport Number", "Status"). Leave empty to extract all columns. Column names should match exactly as they appear in the table header.',
                                      })}
                                    >
                                      <Controller
                                        name={`steps.${index}.step.extractColumns` as any}
                                        control={control}
                                        render={({ field }) => {
                                          const value = field.value || [];
                                          return (
                                            <Select
                                              mode="tags"
                                              {...field}
                                              value={value}
                                              onChange={(newValue) => {
                                                field.onChange(newValue);
                                              }}
                                              placeholder={t(
                                                'workflow.extractColumns.placeholder',
                                                {
                                                  defaultValue:
                                                    'Enter column names (e.g., Passport Number, Status)',
                                                }
                                              )}
                                              tokenSeparators={[',']}
                                              allowClear
                                              style={{ width: '100%' }}
                                            />
                                          );
                                        }}
                                      />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              );
                            })()}

                            {/* Advanced Options */}
                            <Collapse ghost>
                              <Panel
                                header={t('workflow.advancedOptions.title', {
                                  defaultValue: 'Advanced Options',
                                })}
                                key="advanced"
                              >
                                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                  {/* Wait for Page Load */}
                                  <Form.Item
                                    label={t('workflow.waitForPageLoad.label', {
                                      defaultValue: 'Wait for Page Load',
                                    })}
                                  >
                                    <Controller
                                      name={`steps.${index}.step.waitForPageLoad` as any}
                                      control={control}
                                      render={({ field }) => (
                                        <Select
                                          {...field}
                                          placeholder={t('workflow.waitForPageLoad.placeholder', {
                                            defaultValue: 'Select page load event',
                                          })}
                                          allowClear
                                        >
                                          <Select.Option value="load">load</Select.Option>
                                          <Select.Option value="domcontentloaded">
                                            domcontentloaded
                                          </Select.Option>
                                          <Select.Option value="networkidle">
                                            networkidle
                                          </Select.Option>
                                        </Select>
                                      )}
                                    />
                                  </Form.Item>

                                  {/* Wait for Selector */}
                                  <Form.Item
                                    label={t('workflow.waitForSelector.simple.label', {
                                      defaultValue: 'Wait for Selector (Simple)',
                                    })}
                                  >
                                    <Controller
                                      name={`steps.${index}.step.waitForSelector` as any}
                                      control={control}
                                      render={({ field }) => (
                                        <Switch
                                          checked={field.value === true}
                                          onChange={(checked) =>
                                            field.onChange(checked || undefined)
                                          }
                                        />
                                      )}
                                    />
                                    <div
                                      style={{
                                        fontSize: '12px',
                                        color: token.colorTextSecondary,
                                        marginTop: 4,
                                      }}
                                    >
                                      {t('workflow.waitForSelector.simple.help', {
                                        defaultValue:
                                          'Toggle on for simple wait, or configure advanced options below',
                                      })}
                                    </div>
                                  </Form.Item>

                                  {/* Wait for Selector - Advanced (State) - Only show if simple mode is off */}
                                  {step.step.waitForSelector !== true && (
                                    <>
                                      <Form.Item
                                        label={t('workflow.waitForSelector.state.label', {
                                          defaultValue: 'Wait for Selector - State',
                                        })}
                                      >
                                        <Controller
                                          name={`steps.${index}.step.waitForSelector.state` as any}
                                          control={control}
                                          render={({ field }) => (
                                            <Select
                                              {...field}
                                              placeholder={t(
                                                'workflow.waitForSelector.state.placeholder',
                                                { defaultValue: 'Select selector state' }
                                              )}
                                              allowClear
                                            >
                                              <Select.Option value="visible">visible</Select.Option>
                                              <Select.Option value="hidden">hidden</Select.Option>
                                              <Select.Option value="attached">
                                                attached
                                              </Select.Option>
                                              <Select.Option value="detached">
                                                detached
                                              </Select.Option>
                                            </Select>
                                          )}
                                        />
                                      </Form.Item>

                                      {/* Wait for Selector - Timeout */}
                                      <Form.Item
                                        label={t('workflow.waitForSelector.timeout.label', {
                                          defaultValue: 'Wait for Selector - Timeout (ms)',
                                        })}
                                      >
                                        <Controller
                                          name={
                                            `steps.${index}.step.waitForSelector.timeoutMs` as any
                                          }
                                          control={control}
                                          render={({ field }) => (
                                            <InputNumber
                                              {...field}
                                              style={{ width: '100%' }}
                                              min={0}
                                              step={1000}
                                              placeholder={t(
                                                'workflow.waitForSelector.timeout.placeholder',
                                                {
                                                  defaultValue:
                                                    'Enter timeout in milliseconds (e.g., 10000)',
                                                }
                                              )}
                                            />
                                          )}
                                        />
                                      </Form.Item>
                                    </>
                                  )}

                                  {/* Enable Iframe Context */}
                                  <Form.Item
                                    label={t('workflow.iframeContext.label', {
                                      defaultValue: 'Use Iframe Context',
                                    })}
                                  >
                                    <Switch
                                      checked={step.step.iframeContext?.autoDetect || false}
                                      onChange={(checked) => {
                                        if (checked) {
                                          setValue(`steps.${index}.step.iframeContext` as any, {
                                            autoDetect: true,
                                          });
                                        } else {
                                          setValue(
                                            `steps.${index}.step.iframeContext` as any,
                                            undefined
                                          );
                                        }
                                      }}
                                    />
                                    <div
                                      style={{
                                        fontSize: '12px',
                                        color: token.colorTextSecondary,
                                        marginTop: 4,
                                      }}
                                    >
                                      {t('workflow.iframeContext.help', {
                                        defaultValue:
                                          'Enable if this step needs to interact with elements inside an iframe',
                                      })}
                                    </div>
                                  </Form.Item>
                                </Space>
                              </Panel>
                            </Collapse>
                          </Space>
                        </Panel>
                      );
                    })}
                  </Collapse>

                  {/* Add Step Button */}
                  <div style={{ marginTop: 16 }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={addStep} block>
                      Add Step
                    </Button>
                  </div>

                  {/* Action Buttons */}
                  <Divider />
                  <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                    <Button
                      type="primary"
                      onClick={handleNext}
                      size="large"
                      disabled={!isStep1Valid}
                    >
                      Next
                    </Button>
                  </Space>
                </>
              ) : currentStep === 1 ? (
                <>
                  {/* Step 2: Link Dynamic Fields */}
                  <Typography.Title level={4}>{t('workflow.linkDynamicFields')}</Typography.Title>
                  <Divider />

                  {dynamicFields.length === 0 ? (
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '40px',
                      }}
                    >
                      <Typography.Text type="secondary">
                        {t('workflow.noDynamicFields')}
                      </Typography.Text>
                    </div>
                  ) : (
                    <>
                      {/* Data Source Dropdown */}
                      <Form.Item
                        label={t('workflow.mappings.dataSource.label', {
                          defaultValue: 'Data Source',
                        })}
                        required
                      >
                        <Controller
                          name="mappings.dataSource"
                          control={control}
                          rules={{
                            required: t('workflow.mappings.dataSource.required', {
                              defaultValue: 'Data source is required',
                            }),
                          }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              placeholder={t('workflow.mappings.dataSource.placeholder', {
                                defaultValue: 'Select data source',
                              })}
                              status={errors.mappings?.dataSource ? 'error' : ''}
                            >
                              {Object.values(DataSourceEnum).map((source) => (
                                <Select.Option key={source} value={source}>
                                  {source}
                                </Select.Option>
                              ))}
                            </Select>
                          )}
                        />
                        {errors.mappings?.dataSource && (
                          <div style={{ color: token.colorError, marginTop: 4 }}>
                            {errors.mappings.dataSource.message}
                          </div>
                        )}
                      </Form.Item>

                      <Divider />

                      {/* Field Mappings */}
                      <Typography.Title level={5}>
                        {t('workflow.mappings.fields.title', {
                          defaultValue: 'Field Mappings',
                        })}
                      </Typography.Title>
                      <Typography.Text
                        type="secondary"
                        style={{ display: 'block', marginBottom: 16 }}
                      >
                        {t('workflow.mapDynamicFieldsDescription')}
                      </Typography.Text>

                      {dynamicFields.map((field) => (
                        <Form.Item
                          key={field.key}
                          label={`Step ${field.stepOrder} ${field.stepName}`}
                          required
                          style={{ marginBottom: 16 }}
                        >
                          <Controller
                            name={`mappings.fieldsMap.${field.stepOrder}` as any}
                            control={control}
                            rules={{
                              required: `Please select an application key for Step ${field.stepOrder} ${field.stepName}`,
                            }}
                            render={({ field: formField, fieldState }) => (
                              <Select
                                {...formField}
                                placeholder="Select application key"
                                showSearch
                                filterOption={(input, option) =>
                                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                status={fieldState.error ? 'error' : ''}
                                options={ApplicationKeys.map((key) => ({
                                  label: formatLabel(key),
                                  value: key,
                                }))}
                              />
                            )}
                          />
                          {errors.mappings?.fieldsMap?.[String(field.stepOrder)] && (
                            <div style={{ color: token.colorError, marginTop: 4 }}>
                              {
                                errors.mappings.fieldsMap[String(field.stepOrder)]
                                  ?.message as string
                              }
                            </div>
                          )}
                        </Form.Item>
                      ))}
                    </>
                  )}

                  {/* Action Buttons */}
                  <Divider />
                  <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                    <Button onClick={handlePrevious} size="large">
                      Previous
                    </Button>
                    <Button type="primary" onClick={handleNext} size="large">
                      Next
                    </Button>
                  </Space>
                </>
              ) : (
                <>
                  {/* Step 3: Apply Condition (optional) */}
                  <Typography.Title level={4}>
                    {t('workflow.applyCondition.title', { defaultValue: 'Apply Condition' })}
                  </Typography.Title>
                  <Divider />
                  {conditionKeys.map((key, index) => (
                    <Card
                      key={key}
                      size="small"
                      style={{ marginTop: 16 }}
                      bodyStyle={{ padding: 16 }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: 12,
                        }}
                      >
                        <Typography.Text strong>
                          {t('workflow.conditions.itemTitle', {
                            defaultValue: 'Condition {{index}}',
                            index: index + 1,
                          })}
                        </Typography.Text>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            const currentCondition = formValues?.condition || {};
                            const { [key]: _, ...rest } = currentCondition as any;
                            const hasRemaining = Object.keys(rest).length > 0;
                            setValue('condition', (hasRemaining ? rest : undefined) as any);
                          }}
                        />
                      </div>

                      <Form.Item
                        label={t('workflow.conditions.select.label', {
                          defaultValue: 'Select Condition',
                        })}
                        required
                      >
                        <Select
                          value={key}
                          placeholder={t('workflow.conditions.select.placeholder', {
                            defaultValue: 'Select condition',
                          })}
                          onChange={(value) => {
                            const currentCondition = formValues?.condition || {};
                            const { [key]: _, ...rest } = currentCondition as any;
                            const isSkipReLogin = value === WORKFLOW_CONDITION_SKIP_RE_LOGIN;
                            setValue('condition', {
                              ...rest,
                              [value]: isSkipReLogin
                                ? { login_check_step: '', jump_to_step: '' }
                                : value,
                            } as any);
                          }}
                          options={WorkflowConditionKeys.map((optionKey) => ({
                            label: formatConditionLabel(optionKey),
                            value: optionKey,
                            disabled: conditionKeys.includes(optionKey) && optionKey !== key,
                          }))}
                        />
                      </Form.Item>

                      {key === WORKFLOW_CONDITION_SKIP_RE_LOGIN && (
                        <>
                          <Form.Item
                            label={t('workflow.conditions.skipReLogin.loginCheckStep.label', {
                              defaultValue: 'Login Check Step',
                            })}
                            required
                          >
                            <Select
                              value={
                                typeof (formValues?.condition || {})[key] === 'object' &&
                                (formValues?.condition || {})[key] !== null
                                  ? ((formValues?.condition || {})[key] as any)?.login_check_step ||
                                    undefined
                                  : undefined
                              }
                              placeholder={t(
                                'workflow.conditions.skipReLogin.loginCheckStep.placeholder',
                                { defaultValue: 'Select step to check login' }
                              )}
                              onChange={(stepOrder) => {
                                const currentCondition = formValues?.condition || {};
                                const existingValue = currentCondition[key];
                                const isObject =
                                  typeof existingValue === 'object' &&
                                  existingValue !== null &&
                                  !Array.isArray(existingValue);
                                setValue('condition', {
                                  ...currentCondition,
                                  [key]: {
                                    ...(isObject ? existingValue : {}),
                                    login_check_step: String(stepOrder),
                                    jump_to_step:
                                      (isObject && (existingValue as any)?.jump_to_step) || '',
                                  },
                                } as any);
                              }}
                              options={watchedSteps.map((step) => ({
                                label: step.stepName
                                  ? `Step ${step.order} - ${step.stepName}`
                                  : `Step ${step.order}`,
                                value: String(step.order),
                              }))}
                            />
                          </Form.Item>
                          <Form.Item
                            label={t('workflow.conditions.skipReLogin.jumpToStep.label', {
                              defaultValue: 'Jump to Step',
                            })}
                            required
                          >
                            <Select
                              value={
                                typeof (formValues?.condition || {})[key] === 'object' &&
                                (formValues?.condition || {})[key] !== null
                                  ? ((formValues?.condition || {})[key] as any)?.jump_to_step ||
                                    undefined
                                  : undefined
                              }
                              placeholder={t(
                                'workflow.conditions.skipReLogin.jumpToStep.placeholder',
                                { defaultValue: 'Select step to jump to' }
                              )}
                              onChange={(stepOrder) => {
                                const currentCondition = formValues?.condition || {};
                                const existingValue = currentCondition[key];
                                const isObject =
                                  typeof existingValue === 'object' &&
                                  existingValue !== null &&
                                  !Array.isArray(existingValue);
                                setValue('condition', {
                                  ...currentCondition,
                                  [key]: {
                                    ...(isObject ? existingValue : {}),
                                    login_check_step:
                                      (isObject && (existingValue as any)?.login_check_step) || '',
                                    jump_to_step: String(stepOrder),
                                  },
                                } as any);
                              }}
                              options={watchedSteps.map((step) => ({
                                label: step.stepName
                                  ? `Step ${step.order} - ${step.stepName}`
                                  : `Step ${step.order}`,
                                value: String(step.order),
                              }))}
                            />
                          </Form.Item>
                        </>
                      )}
                    </Card>
                  ))}

                  <div style={{ marginTop: 24 }}>
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      block
                      onClick={() => {
                        const existingKeys = conditionKeys;
                        const nextKey = WorkflowConditionKeys.find(
                          (key) => !existingKeys.includes(key)
                        );
                        if (!nextKey) {
                          message.warning(
                            t('workflow.conditions.allAdded', {
                              defaultValue: 'All available conditions have already been added.',
                            })
                          );
                          return;
                        }
                        const isSkipReLogin = nextKey === WORKFLOW_CONDITION_SKIP_RE_LOGIN;
                        setValue('condition', {
                          ...(formValues?.condition || {}),
                          [nextKey]: isSkipReLogin
                            ? { login_check_step: '', jump_to_step: '' }
                            : nextKey,
                        } as any);
                      }}
                    >
                      {t('workflow.conditions.addButton', { defaultValue: 'Add Condition' })}
                    </Button>
                  </div>

                  {/* Action Buttons */}
                  <Divider />
                  <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                    <Button onClick={handlePrevious} size="large">
                      Previous
                    </Button>
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      onClick={handleSubmit}
                      loading={isLoading}
                      size="large"
                    >
                      Submit
                    </Button>
                  </Space>
                </>
              )}
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={10} xl={8}>
          <Card className="shadow-lg" style={{ height: '100%' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
              }}
            >
              <Typography.Title level={4}>JSON Preview</Typography.Title>
              <Button icon={<CopyOutlined />} onClick={copyJsonToClipboard}>
                Copy
              </Button>
            </div>
            <Divider style={{ marginTop: 0 }} />
            <pre
              style={{
                fontFamily: 'monospace',
                fontSize: '12px',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                margin: 0,
                padding: '12px',
                backgroundColor: token.colorBgLayout,
                borderRadius: token.borderRadius,
                maxHeight: 'calc(100vh - 300px)',
                overflow: 'auto',
              }}
            >
              {currentStep > 0
                ? JSON.stringify(
                    {
                      name: formValues?.name || '',
                      steps: JSON.parse(generatedJson || '[]'),
                      mappings: formValues?.mappings,
                      condition: formValues?.condition,
                    },
                    null,
                    2
                  )
                : generatedJson || '[]'}
            </pre>
          </Card>
        </Col>
      </Row>

      {/* JSON Preview Modal */}
      <Modal
        title="Generated JSON"
        open={jsonModalVisible}
        onCancel={() => setJsonModalVisible(false)}
        width={800}
        footer={[
          <Button key="copy" icon={<CopyOutlined />} onClick={copyJsonToClipboard}>
            Copy to Clipboard
          </Button>,
          <Button key="close" type="primary" onClick={() => setJsonModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        <TextArea
          value={generatedJson}
          rows={20}
          readOnly
          style={{ fontFamily: 'monospace', fontSize: '12px' }}
        />
      </Modal>
    </>
  );
};

export default WorkflowForm;
